import { useCanvasCursor } from '../use-canvas-cursor'
import { useCanvasPan } from '../use-canvas-pan'
import { useCreationPreviews } from '../use-creation-previews'
import { useDragCreateModifierKeys } from '../use-drag-create-modifier-keys'
import { useDragCreateSmartGuides } from '../use-drag-create-smart-guides'
import { useEditModeClickAway } from '../use-edit-mode-click-away'
import { useMarqueeSelect } from '../use-marquee-select'
import { useMarqueeZoom } from '../use-marquee-zoom'
import { usePasteboardPointer } from '../use-pasteboard-pointer'
import { useRafCleanup } from '../use-raf-cleanup'
import { useShapeDragCreate } from '../use-shape-drag-create'
import { useSnapGuides } from '../use-snap-guides'
import { useTextDragCreate } from '../use-text-drag-create'
import { useTextLinkDrag } from '../use-text-link-drag'
import type {
  CanvasEditModes,
  CanvasFoundation,
  CanvasSelectionCommands,
  CanvasTextLinking,
  CanvasToolModes,
  CanvasViewport,
} from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * Pointer input on the pasteboard: one hook per gesture (marquee zoom and select, text and shape
 * drag-create, text-link drag, edit-mode click-away, panning) and `usePasteboardPointer`, which
 * routes events between them. Also the creation previews, the cursor, and snap and smart guides
 * while drag-creating.
 */
export function useCanvasPointerGestures(
  {
    pageViewMode,
    snapToGrid,
    showGuides,
    snapToGuides,
    snapGuideDistance,
    columns,
    rows,
    colGutter,
    rowGutter,
    bleedWidth,
    bleedHeight,
    trimWidth,
    trimHeight,
    bleedMargin,
    marginInside,
    marginOutside,
    marginTop,
    marginBottom,
    showLeftPageGuides,
    showRightPageGuides,
    selectedShape,
    onNodeEditModeChange,
    onBackgroundClick,
    onActivateSelectTool,
    mirrorSpineAxisX,
    mirrorMoveHoverActive,
    spreadId,
    newTextBoxKerning,
    newTextBoxTracking,
    newTextBoxUnderline,
    newTextBoxAlignment,
    defaultFontSize,
    onTextFramesLinked,
    onCreateLinkedTextFrame,
  }: ResolvedCenterCanvasConfig,
  {
    resolvedDefaultFontFamilyId,
    resolvedDefaultFontId,
    newShapeTextSeedStyle,
    internalShapeManagerRef,
    scrollRef,
    pasteboardRef,
    setActiveSmartGuides,
    effectiveSmartGuides,
    isPointerOnEditingShapeText,
    tryEnterShapeTextEditFromPointer,
    setIsSpacePanActive,
    isDragPanning,
    setIsDragPanning,
    isPanInteractionActive,
    panDragRef,
    panFrameRef,
    panScrollTargetRef,
    guides,
    localDragGuides,
    setSelectedGuideIds,
  }: Pick<
    CanvasFoundation,
    | 'resolvedDefaultFontFamilyId'
    | 'resolvedDefaultFontId'
    | 'newShapeTextSeedStyle'
    | 'internalShapeManagerRef'
    | 'scrollRef'
    | 'pasteboardRef'
    | 'setActiveSmartGuides'
    | 'effectiveSmartGuides'
    | 'isPointerOnEditingShapeText'
    | 'tryEnterShapeTextEditFromPointer'
    | 'setIsSpacePanActive'
    | 'isDragPanning'
    | 'setIsDragPanning'
    | 'isPanInteractionActive'
    | 'panDragRef'
    | 'panFrameRef'
    | 'panScrollTargetRef'
    | 'guides'
    | 'localDragGuides'
    | 'setSelectedGuideIds'
  >,
  {
    scaleRef,
    documentGridSnapStepPt,
    pageTrimWidth,
    gridSnapLiveRects,
    getCanvasPointFromPointerEvent,
    syncRulerScrollTransforms,
  }: Pick<
    CanvasViewport,
    | 'scaleRef'
    | 'documentGridSnapStepPt'
    | 'pageTrimWidth'
    | 'gridSnapLiveRects'
    | 'getCanvasPointFromPointerEvent'
    | 'syncRulerScrollTransforms'
  >,
  {
    textLinking,
    setTextLinking,
    isTextLinkingActive,
    setTextToolHoverFrameId,
    linkedTextDragStartRef,
    announceTextLink,
    findLinkableFrameAtDocPoint,
  }: Pick<
    CanvasTextLinking,
    | 'textLinking'
    | 'setTextLinking'
    | 'isTextLinkingActive'
    | 'setTextToolHoverFrameId'
    | 'linkedTextDragStartRef'
    | 'announceTextLink'
    | 'findLinkableFrameAtDocPoint'
  >,
  {
    isInShapeCreationMode,
    isInPenToolMode,
    isInTextCreationMode,
  }: Pick<CanvasToolModes, 'isInShapeCreationMode' | 'isInPenToolMode' | 'isInTextCreationMode'>,
  {
    setNodeEditShapeId,
    nodeEditShapeData,
    setNodeEditShapeData,
    nodeEditShapeIdRef,
    isNodeEditMode,
    isPenEditMode,
    penEditHoverKind,
    setCropShapeId,
    cropShapeData,
    setCropShapeData,
    isCropMode,
    clearShapeSelectionWithHistory,
    selectShapesWithHistoryAndTextMeta,
    exitGroupIsolationMode,
    activeIsolationGroupId,
    canSelectShapes,
  }: Pick<
    CanvasEditModes,
    | 'setNodeEditShapeId'
    | 'nodeEditShapeData'
    | 'setNodeEditShapeData'
    | 'nodeEditShapeIdRef'
    | 'isNodeEditMode'
    | 'isPenEditMode'
    | 'penEditHoverKind'
    | 'setCropShapeId'
    | 'cropShapeData'
    | 'setCropShapeData'
    | 'isCropMode'
    | 'clearShapeSelectionWithHistory'
    | 'selectShapesWithHistoryAndTextMeta'
    | 'exitGroupIsolationMode'
    | 'activeIsolationGroupId'
    | 'canSelectShapes'
  >,
  { isEditableTarget }: Pick<CanvasSelectionCommands, 'isEditableTarget'>
) {
  // ── Pasteboard gestures, one hook each; `usePasteboardPointer` (below) routes to them ──
  const {
    isMarqueeZoomMode,
    setIsMarqueeZoomMode,
    zMarqueeKeyHeldRef,
    zMarqueeKeyHeld,
    setZMarqueeKeyHeld,
    marqueeZoom,
    setMarqueeZoom,
    applyZoomToCanvasRectRef,
    handleMarqueeZoomPointerDownCapture,
    handleMarqueeZoomPointerMove,
    handleMarqueeZoomPointerUp,
  } = useMarqueeZoom({ internalShapeManagerRef, scaleRef, getCanvasPointFromPointerEvent })

  const {
    textDragCreateRef,
    textDragCreate,
    setTextDragCreate,
    textDragRafRef,
    handleTextDragCreatePointerDown,
    handleTextDragCreatePointerMove,
    handleTextDragCreatePointerUp,
  } = useTextDragCreate({
    internalShapeManagerRef,
    isInTextCreationMode,
    scaleRef,
    getCanvasPointFromPointerEvent,
    isPointerOnEditingShapeText,
    tryEnterShapeTextEditFromPointer,
    clearShapeSelectionWithHistory,
    setSelectedGuideIds,
    onBackgroundClick,
    onActivateSelectTool,
    newShapeTextSeedStyle,
    newTextBoxKerning,
    newTextBoxTracking,
    newTextBoxUnderline,
    newTextBoxAlignment,
    defaultFontSize,
    resolvedDefaultFontFamilyId,
    resolvedDefaultFontId,
    spreadId,
  })

  const {
    handleTextLinkPointerDown,
    handleTextLinkPointerMove,
    handleTextToolHoverPointerMove,
    handleTextLinkPointerUp,
  } = useTextLinkDrag({
    textLinking,
    setTextLinking,
    linkedTextDragStartRef,
    findLinkableFrameAtDocPoint,
    announceTextLink,
    setTextToolHoverFrameId,
    isInTextCreationMode,
    textDragCreateRef,
    scaleRef,
    getCanvasPointFromPointerEvent,
    onTextFramesLinked,
    onCreateLinkedTextFrame,
  })

  const {
    marquee,
    setMarquee,
    handleMarqueeSelectPointerDown,
    handleMarqueeSelectPointerMove,
    handleMarqueeSelectPointerUp,
  } = useMarqueeSelect({
    internalShapeManagerRef,
    canSelectShapes,
    activeIsolationGroupId,
    scaleRef,
    getCanvasPointFromPointerEvent,
    isPointerOnEditingShapeText,
    clearShapeSelectionWithHistory,
    selectShapesWithHistoryAndTextMeta,
    setSelectedGuideIds,
    onBackgroundClick,
  })

  const {
    dragCreate,
    setDragCreate,
    dragCreateRef,
    dragRafRef,
    layoutSnapLinesForDragCreateRef,
    computeShapeCreationParams,
    buildShapePathCommands,
    handleShapeDragCreatePointerDown,
    handleShapeDragCreatePointerMove,
    handleShapeDragCreatePointerUp,
  } = useShapeDragCreate({
    internalShapeManagerRef,
    isInShapeCreationMode,
    selectedShape,
    scaleRef,
    getCanvasPointFromPointerEvent,
    showGuides,
    snapToGuides,
    snapGuideDistance,
    snapToGrid,
    documentGridSnapStepPt,
    gridSnapLiveRects,
    setActiveSmartGuides,
  })

  const { handleEditModeClickAwayPointerDown } = useEditModeClickAway({
    internalShapeManagerRef,
    getCanvasPointFromPointerEvent,
    isNodeEditMode,
    isPenEditMode,
    nodeEditShapeData,
    nodeEditShapeIdRef,
    setNodeEditShapeId,
    setNodeEditShapeData,
    onNodeEditModeChange,
    isCropMode,
    cropShapeData,
    setCropShapeId,
    setCropShapeData,
  })

  const canvasCursor = useCanvasCursor({
    selectedShape,
    isDragPanning,
    isPanInteractionActive,
    isTextLinkingActive,
    textDragCreate,
    isMarqueeZoomMode,
    zMarqueeKeyHeld,
    isPenEditMode,
    penEditHoverKind,
    isInPenToolMode,
    isNodeEditMode,
    isInShapeCreationMode,
    isInTextCreationMode,
  })

  useRafCleanup({ dragRafRef, textDragRafRef, panFrameRef })

  useDragCreateModifierKeys({ dragCreate, dragCreateRef, setDragCreate })

  const {
    applyPanScroll,
    endPanDrag,
    handlePanPointerDown,
    handleMiddlePanPointerDown,
    handlePanDragPointerMove,
    handlePanDragPointerUp,
  } = useCanvasPan({
    scrollRef,
    internalShapeManagerRef,
    panDragRef,
    panFrameRef,
    panScrollTargetRef,
    setIsDragPanning,
    setIsSpacePanActive,
    isPanInteractionActive,
    isEditableTarget,
    syncRulerScrollTransforms,
  })

  // ── Unified multi-select chrome move handlers ──

  const {
    handlePasteboardPointerDown,
    handlePasteboardPointerMove,
    handlePasteboardPointerUp,
    handlePasteboardPointerCancel,
    handleScrollerPointerDown,
  } = usePasteboardPointer({
    pasteboardRef,
    bleedWidth,
    bleedHeight,
    activeIsolationGroupId,
    exitGroupIsolationMode,
    getCanvasPointFromPointerEvent,
    handleTextLinkPointerDown,
    handleTextLinkPointerMove,
    handleTextToolHoverPointerMove,
    handleTextLinkPointerUp,
    handleMiddlePanPointerDown,
    handlePanPointerDown,
    handlePanDragPointerMove,
    handlePanDragPointerUp,
    endPanDrag,
    handleEditModeClickAwayPointerDown,
    handleMarqueeZoomPointerMove,
    handleMarqueeZoomPointerUp,
    setMarqueeZoom,
    handleMarqueeSelectPointerDown,
    handleMarqueeSelectPointerMove,
    handleMarqueeSelectPointerUp,
    setMarquee,
    handleTextDragCreatePointerDown,
    handleTextDragCreatePointerMove,
    handleTextDragCreatePointerUp,
    textDragCreateRef,
    textDragRafRef,
    setTextDragCreate,
    handleShapeDragCreatePointerDown,
    handleShapeDragCreatePointerMove,
    handleShapeDragCreatePointerUp,
    dragCreateRef,
    dragRafRef,
    setDragCreate,
  })

  const {
    dragPreviewShape,
    mirrorMovePreviewShapes,
    marqueeRect,
    textDragPreviewRect,
    marqueeZoomRect,
  } = useCreationPreviews({
    internalShapeManagerRef,
    scaleRef,
    dragCreate,
    selectedShape,
    computeShapeCreationParams,
    buildShapePathCommands,
    snapToGrid,
    documentGridSnapStepPt,
    gridSnapLiveRects,
    mirrorMoveHoverActive,
    mirrorSpineAxisX,
    marquee,
    textDragCreate,
    marqueeZoom,
  })

  const { visibleGuides, snapGuides } = useSnapGuides({
    guides,
    localDragGuides,
    layoutSnapLinesForDragCreateRef,
    pageTrimWidth,
    trimWidth,
    trimHeight,
    bleedMargin,
    marginTop,
    marginBottom,
    marginInside,
    marginOutside,
    showLeftPageGuides,
    showRightPageGuides,
    columns,
    rows,
    colGutter,
    rowGutter,
    pageViewMode,
  })

  useDragCreateSmartGuides({
    internalShapeManagerRef,
    dragPreviewShape,
    effectiveSmartGuides,
    snapGuideDistance,
    snapGuides,
    snapToGuides,
    layoutSnapLinesForDragCreateRef,
    setActiveSmartGuides,
  })

  return {
    setIsMarqueeZoomMode,
    zMarqueeKeyHeldRef,
    setZMarqueeKeyHeld,
    setMarqueeZoom,
    applyZoomToCanvasRectRef,
    handleMarqueeZoomPointerDownCapture,
    dragCreate,
    applyPanScroll,
    handlePasteboardPointerDown,
    handlePasteboardPointerMove,
    handlePasteboardPointerUp,
    handlePasteboardPointerCancel,
    handleScrollerPointerDown,
    dragPreviewShape,
    mirrorMovePreviewShapes,
    marqueeRect,
    textDragPreviewRect,
    marqueeZoomRect,
    visibleGuides,
    snapGuides,
    canvasCursor,
  }
}
